import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { forwardRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useFormatPersonName } from "../../../hooks/useNameFormat";
import { FONT_SIZE, HOLIDAY_GROUPS, INK, SHADOW } from "../../../theme/tokens";
import type { TeamNurse } from "../../../utils/models";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import MetaChip from "../../UIToolkit/Badges/MetaChip";
import { EmptyState } from "../../UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../UIToolkit/molecules/SettingsSection";
import {
	CONFIG_TABLE_BODY_CELL_SX,
	CONFIG_TABLE_BODY_ROW_SX,
	CONFIG_TABLE_CONTAINER_SX,
	CONFIG_TABLE_HEADER_CELL_SX,
} from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { type HolidayStaffFilter, toHolidayChangePerson } from "../teamHolidays";
import { HolidayGroupSelect } from "./HolidayGroupSelect";
import { HolidayNotAssignedFooter } from "./HolidayNotAssignedFooter";
import { HolidaySectionError } from "./HolidaySectionError";
import type { HolidayGroup, HolidayGroupChangePerson, HolidayPerson } from "./holidayApi";
import { HOLIDAY_NONE, mostFrequentShiftText } from "./holidayFormat";

type Props = {
	groups: HolidayGroup[];
	/** Active people; undefined while loading. */
	people: HolidayPerson[] | undefined;
	/** The people or the roster couldn't load. */
	isError: boolean;
	onRetry: () => void;
	/** The department's roster, for names and jobs; undefined while loading. */
	roster: TeamNurse[] | undefined;
	canEdit: boolean;
	onChangeGroup: (person: HolidayGroupChangePerson, groupId: string | null) => void;
	/** Opens the Team roster on these people, where the bulk action can place many at once. */
	onShowOnTeam: (filter: HolidayStaffFilter) => void;
};

/** Text cells line up with their column header, which sits a notch further in than an in-cell control. */
const TEXT_CELL_PADDING = { px: 1.5 } as const;

const COLUMN_KEYS = ["name", "job", "holidays.notAssigned.column.shift", "holidays.notAssigned.column.group"] as const;

/**
 * Rows shown at first, and added by each "Show more". Every row carries a group dropdown, and mounting a few
 * hundred of them at once froze the tab for seconds on a large department; placing many people at once is
 * the Team list's bulk action.
 */
const ROWS_PER_PAGE = 25;

/** Everyone not assigned, on every day part and in every job. */
const NOT_ASSIGNED_FILTER: HolidayStaffFilter = { groupId: HOLIDAY_NONE, dayPartKey: null, jobName: null };

/** Active staff with no holiday group — on holidays they keep their usual shifts — and a quick way to place them. */
export const HolidayNotAssignedSection = forwardRef<HTMLDivElement, Props>(
	({ groups, people, isError, onRetry, roster, canEdit, onChangeGroup, onShowOnTeam }, ref) => {
		const { t } = useTranslation();
		const { track } = useAnalytics();
		const formatPersonName = useFormatPersonName();
		const [shownCount, setShownCount] = useState(ROWS_PER_PAGE);
		const rows = useMemo(() => {
			if (people === undefined || roster === undefined) return undefined;
			const nurseById = new Map(roster.map((nurse) => [nurse.id, nurse]));
			return people
				.filter((person) => person.groupId === null)
				.flatMap((person) => {
					const nurse = nurseById.get(person.nurseId);
					// Both lists are the department's people; anyone the roster hasn't loaded yet appears once it has.
					return nurse === undefined ? [] : [{ person, changePerson: toHolidayChangePerson(nurse, formatPersonName) }];
				})
				.sort((a, b) => a.changePerson.name.localeCompare(b.changePerson.name));
		}, [people, roster, formatPersonName]);

		const renderBody = () => {
			if (isError) return <HolidaySectionError onRetry={onRetry} dataTestId="holiday-not-assigned-error" />;
			if (rows === undefined) return <JumpingDots size={6} />;
			if (rows.length === 0)
				return <EmptyState icon={<TaskAltOutlinedIcon />} title={t("holidays.notAssigned.empty")} dataTestId="holiday-not-assigned-empty" />;
			const shownRows = rows.slice(0, shownCount);
			const hiddenCount = rows.length - shownRows.length;
			return (
				<>
					<Box sx={CONFIG_TABLE_CONTAINER_SX}>
						<Table size="small" data-testid="holiday-not-assigned-table">
							<TableHead>
								<TableRow>
									{COLUMN_KEYS.map((key) => (
										<TableCell key={key} sx={CONFIG_TABLE_HEADER_CELL_SX}>
											{t(key)}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{shownRows.map(({ person, changePerson }) => {
									const shift = mostFrequentShiftText(person.calculated);
									return (
										<TableRow key={person.nurseId} sx={CONFIG_TABLE_BODY_ROW_SX} data-testid={`holiday-not-assigned-row-${person.nurseId}`}>
											<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, TEXT_CELL_PADDING]}>
												<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>{changePerson.name}</Typography>
											</TableCell>
											<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, TEXT_CELL_PADDING, { color: INK.SECONDARY, fontSize: FONT_SIZE.EMPHASIS }]}>
												{/* DEFAULT: someone with no job shows a dash, as every empty cell does. */}
												{changePerson.jobLabel ?? "—"}
											</TableCell>
											<TableCell
												sx={[
													CONFIG_TABLE_BODY_CELL_SX,
													TEXT_CELL_PADDING,
													{ color: shift === null ? INK.MUTED : INK.SECONDARY, fontSize: FONT_SIZE.SECONDARY },
												]}
											>
												{shift ?? t("holidays.noShiftsLast6Weeks")}
											</TableCell>
											<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { minWidth: HOLIDAY_GROUPS.SELECT_MIN_WIDTH_PX }]}>
												<HolidayGroupSelect
													groups={groups}
													value={null}
													onChange={(groupId) => onChangeGroup(changePerson, groupId)}
													allowNone
													variant="table"
													label={t("holidays.groupCellAria", { name: changePerson.name })}
													showLabel={false}
													disabled={!canEdit}
													dataTestId={`holiday-not-assigned-group-${person.nurseId}`}
												/>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</Box>
					{/* A long list gets a way to the Team list, whose bulk action places many people at once. */}
					{rows.length > ROWS_PER_PAGE && (
						<HolidayNotAssignedFooter
							shownCount={shownRows.length}
							totalCount={rows.length}
							showMoreCount={Math.min(ROWS_PER_PAGE, hiddenCount)}
							onShowMore={() => setShownCount((count) => count + ROWS_PER_PAGE)}
							onOpenOnTeam={() => {
								track("team_holidays_not_assigned_open_team", { count: rows.length });
								onShowOnTeam(NOT_ASSIGNED_FILTER);
							}}
						/>
					)}
				</>
			);
		};

		return (
			<Box ref={ref} component="div" sx={{ scrollMarginTop: (theme) => theme.spacing(2) }}>
				<SettingsSection
					title={t("holidays.notAssigned.title")}
					icon={<PersonOffOutlinedIcon />}
					description={t("holidays.notAssigned.description")}
					action={
						rows === undefined ? undefined : (
							<MetaChip label={t("holidays.notAssigned.count", { count: rows.length })} tone={rows.length > 0 ? "warning" : "success"} />
						)
					}
					layout="stack"
					dataTestId="holiday-not-assigned-section"
					sx={{ boxShadow: SHADOW.PANEL_SOFT }}
				>
					{renderBody()}
				</SettingsSection>
			</Box>
		);
	},
);
HolidayNotAssignedSection.displayName = "HolidayNotAssignedSection";
